/**
 * What changed between two versions of a report, section by section.
 *
 * A section is found again by its id, which holds as long as it is the same
 * section: an edit keeps it, a move keeps it, a copy does not. So a section is
 * *added* (its id was not there), *modified* (it says something else now),
 * *moved* (it says the same under another parent) or, of those that were there,
 * *removed* (its id has gone). A report imported again has new ids throughout
 * and so reads as entirely changed, which is the truth of it.
 *
 * What a group says of itself is its own fields; what it holds is judged
 * section by section, so adding a page to a chapter does not make the chapter
 * "modified". Whether it is open or closed is how it is shown, not what it says.
 *
 * Pure. Two trees in, a verdict per section out.
 */

import type { Section } from '@/domain/section'
import { isGroup } from '@/domain/section'

export type ChangeKind = 'added' | 'modified' | 'moved'

/** A section that was there before and is not now. */
export interface RemovedSection {
  readonly section: Section
  /** The group it was in, or null at the top of the document. */
  readonly parentId: string | null
}

export interface SectionChanges {
  /** By section id, for the sections that are in the report now. */
  readonly changes: ReadonlyMap<string, ChangeKind>
  /** The sections that are gone, outermost only: a removed group is one entry. */
  readonly removed: readonly RemovedSection[]
  /** How many sections differ in all, what is gone included. */
  readonly count: number
}

/** The ways a section is shown rather than said: left out of what is compared. */
const SHOWN_NOT_SAID = new Set(['collapsed', 'children'])

const fingerprints = new WeakMap<object, string>()

function stable(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, inner]) => inner !== undefined)
      .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
    return `{${entries.map(([key, inner]) => `${JSON.stringify(key)}:${stable(inner)}`).join(',')}}`
  }
  return JSON.stringify(value) ?? 'null'
}

/**
 * What a section says, as text that is the same for the same content.
 *
 * Remembered by the section itself: a report is replaced wholesale on every
 * edit but the sections nobody touched are the same objects, so comparing a
 * long report to a version again after one keystroke costs one section.
 */
export function fingerprint(section: Section): string {
  const known = fingerprints.get(section)
  if (known !== undefined) return known

  const said = Object.fromEntries(
    Object.entries(section).filter(([key]) => !SHOWN_NOT_SAID.has(key)),
  )
  const text = stable(said)
  fingerprints.set(section, text)
  return text
}

interface Placed {
  readonly section: Section
  readonly parentId: string | null
}

function placed(
  sections: readonly Section[],
  parentId: string | null = null,
  into: Map<string, Placed> = new Map(),
): Map<string, Placed> {
  for (const section of sections) {
    into.set(section.id, { section, parentId })
    if (isGroup(section)) placed(section.children, section.id, into)
  }
  return into
}

/** What differs between a version kept earlier and the report as it is. */
export function diffSections(
  before: readonly Section[],
  after: readonly Section[],
): SectionChanges {
  const was = placed(before)
  const is = placed(after)

  const changes = new Map<string, ChangeKind>()
  for (const [id, now] of is) {
    const then = was.get(id)
    if (!then) changes.set(id, 'added')
    else if (fingerprint(then.section) !== fingerprint(now.section)) changes.set(id, 'modified')
    else if (then.parentId !== now.parentId) changes.set(id, 'moved')
  }

  // Outermost only: what was inside a removed group went with it.
  const removed: RemovedSection[] = []
  for (const [id, then] of was) {
    if (is.has(id)) continue
    const parentGone = then.parentId !== null && !is.has(then.parentId)
    if (!parentGone) removed.push({ section: then.section, parentId: then.parentId })
  }

  return { changes, removed, count: changes.size + removed.length }
}
